'use client'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import { Button } from '@ui/base/button'

import { container } from './typography'

/** Насколько страница прокручена, от 0 до 1. В самом низу знаменатель нулевой — тогда считаем «до конца». */
const readShare = () => {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight
  return scrollable > 0 ? Math.min(window.scrollY / scrollable, 1) : 1
}

export type LandingSection = { id: string; label: string }

/**
 * Липкая полоса лендинга: переходы к разделам, главное действие и тонкая линия прочитанного.
 *
 * Линия растягивается по ширине окна, поэтому её длина меняется не свойством `width`, а
 * масштабированием: браузер пересчитывает такую полосу без повторной раскладки страницы.
 */
export const LandingAnchorNav = ({
  actionHref,
  actionLabel,
  sections,
}: {
  actionHref: string
  actionLabel: string
  sections: LandingSection[]
}) => {
  const progressLine = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const draw = () => {
      if (progressLine.current) {
        progressLine.current.style.transform = `scaleX(${readShare()})`
      }
    }

    draw()
    window.addEventListener('scroll', draw, { passive: true })

    // Высота страницы меняется и без изменения размера окна: приходят серверные числа, читатель
    // раскрывает ответ. Без такого наблюдателя доля прочитанного считалась бы по прежней высоте
    // до следующей прокрутки.
    const observer = new ResizeObserver(draw)
    observer.observe(document.documentElement)

    return () => {
      window.removeEventListener('scroll', draw)
      observer.disconnect()
    }
  }, [])

  return (
    <div className="bg-background/92 sticky top-0 z-20 border-b backdrop-blur-md">
      <nav aria-label="Разделы страницы" className={`${container} flex items-center gap-5 py-2.5 sm:gap-7`}>
        {/* На узком экране переходы к разделам скрыты: рядом с кнопкой они сжимаются до обрезанных
            слов, а до самих разделов там всё равно проще долистать. */}
        <ul className="hidden items-center gap-5 sm:flex sm:gap-7">
          {sections.map((section) => (
            <li key={section.id}>
              <Link
                href={`#${section.id}`}
                className="text-section-foreground hover:text-foreground text-[14px] whitespace-nowrap transition-colors"
              >
                {section.label}
              </Link>
            </li>
          ))}
        </ul>
        <Button size="sm" className="w-full sm:ml-auto sm:w-auto" render={<Link href={actionHref} />}>
          {actionLabel}
        </Button>
      </nav>
      {/* Начальное сжатие задано стилем, а не классом Tailwind: класс scale-x-0 пишет свойство
          `scale`, и оно продолжает обнулять полосу поверх нашего преобразования. */}
      <div
        ref={progressLine}
        aria-hidden="true"
        className="bg-primary h-0.5 origin-left will-change-transform"
        style={{ transform: 'scaleX(0)' }}
      />
    </div>
  )
}
